import { useIsCurrentUserReviewer } from './user/useIsCurrentUserReviewer';
import { BrandBriefsStoreContainer } from './brandBriefs';
import { BrandTermsStoreContainer } from './brandTerms';
import { BrandVoicesStoreContainer } from './brandVoices';
import { CommonStoreContainer } from './common';
import { ComplianceStoreContainer } from './compliance';
import { CustomerStoreContainer, useCurrentCustomer, useCurrentCustomerId } from './customer';
import { CustomerMemberStoreContainer } from './customerMember';
import { CustomerPersonaStoreContainer } from './customerPersona';
import { CustomModelsStoreContainer } from './customModels';
import { DebugStoreContainer } from './debug';
import { FormatStoreContainer } from './format';
import { FreestyleToolsContainer } from './freestyleTools';
import { GenerationToolStoreContainer } from './generationTool';
import { ImageStyleStoreContainer } from './imageStyle';
import { MultiPromptTemplatesContainer } from './multiPromptTemplates';
import { NotificationStoreContainer, useNotificationActions } from './notification';
import { PerformanceStoreContainer } from './performance';
import { ProjectStoreContainer } from './project';
import { ProjectFolderStoreContainer } from './projectFolder';
import { ResourceStoreContainer } from './resource';
import { SystemStoreContainer } from './system';
import { TalkingPointsStoreContainer } from './talkingPoints';
import { TemplatesStoreContainer } from './templates';
import { ToneOfVoiceStoreContainer } from './toneOfVoice';
import { UiStoreContainer } from './ui';
import { useCurrentUser, useCurrentUserId, UserStoreContainer } from './user';
import { Container as VariationStoreContainer } from './variation';
import { VisualLanguageStoreContainer } from './visualLanguage';
import { useCurrentWorkspaceId, WorkspaceStoreContainer } from './workspace';

const DataStoresContainers = ({ children }) => {
  const currentUser = useCurrentUser();
  const currentCustomer = useCurrentCustomer();
  const notifications = useNotificationActions();
  const currentWorkspaceId = useCurrentWorkspaceId();

  return (
    <CustomerMemberStoreContainer
      isGlobal
      currentCustomerId={currentCustomer?.id}
    >
      <CommonStoreContainer
        isGlobal
        currentUserConfirmed={currentUser?.isConfirmed}
        notifications={notifications}
      >
        <PerformanceStoreContainer
          isGlobal
          currentCustomerId={currentCustomer?.id}
          currentWorkspaceId={currentWorkspaceId}
          notifications={notifications}
          currentUserId={currentUser?.id}
        >
          <ResourceStoreContainer
            isGlobal
            currentCustomerId={currentCustomer?.id}
            currentWorkspaceId={currentWorkspaceId}
          >
            <TalkingPointsStoreContainer
              isGlobal
              currentCustomerId={currentCustomer?.id}
            >
              <BrandVoicesStoreContainer
                isGlobal
                currentCustomerId={currentCustomer?.id}
                currentWorkspaceId={currentWorkspaceId}
                notifications={notifications}
              >
                <BrandTermsStoreContainer
                  isGlobal
                  currentCustomerId={currentCustomer?.id}
                  notifications={notifications}
                >
                  <GenerationToolStoreContainer isGlobal>
                    <MultiPromptTemplatesContainer
                      isGlobal
                      currentCustomerId={currentCustomer?.id}
                      currentWorkspaceId={currentWorkspaceId}
                    >
                      <TemplatesStoreContainer isGlobal>
                        <FreestyleToolsContainer
                          isGlobal
                          currentCustomerId={currentCustomer?.id}
                          currentWorkspaceId={currentWorkspaceId}
                        >
                          <CustomerPersonaStoreContainer
                            isGlobal
                            currentCustomerId={currentCustomer?.id}
                            notifications={notifications}
                          >
                            <BrandBriefsStoreContainer
                              isGlobal
                              currentCustomerId={currentCustomer?.id}
                            >
                              <CustomModelsStoreContainer
                                isGlobal
                                currentCustomerId={currentCustomer?.id}
                                notifications={notifications}
                              >
                                <ToneOfVoiceStoreContainer
                                  isGlobal
                                  currentCustomerId={currentCustomer?.id}
                                >
                                  <VariationStoreContainer isGlobal>
                                    <ProjectFolderStoreContainer
                                      currentWorkspaceId={currentWorkspaceId}
                                      isGlobal
                                    >
                                      <FormatStoreContainer isGlobal>
                                        <VisualLanguageStoreContainer
                                          isGlobal
                                          currentCustomerId={currentCustomer?.id}
                                        >
                                          <ImageStyleStoreContainer
                                            generalStyles={currentCustomer?.globalImageStyles}
                                            isGlobal
                                          >
                                            <ComplianceStoreContainer isGlobal>
                                              {children}
                                            </ComplianceStoreContainer>
                                          </ImageStyleStoreContainer>
                                        </VisualLanguageStoreContainer>
                                      </FormatStoreContainer>
                                    </ProjectFolderStoreContainer>
                                  </VariationStoreContainer>
                                </ToneOfVoiceStoreContainer>
                              </CustomModelsStoreContainer>
                            </BrandBriefsStoreContainer>
                          </CustomerPersonaStoreContainer>
                        </FreestyleToolsContainer>
                      </TemplatesStoreContainer>
                    </MultiPromptTemplatesContainer>
                  </GenerationToolStoreContainer>
                </BrandTermsStoreContainer>
              </BrandVoicesStoreContainer>
            </TalkingPointsStoreContainer>
          </ResourceStoreContainer>
        </PerformanceStoreContainer>
      </CommonStoreContainer>
    </CustomerMemberStoreContainer>
  );
};

// A reviewer reads only the projects shared with them, so the account's data stores are not
// mounted for one. Their global state outlives the unmount and is loaded again on the next mount.
const AccountDataStoresContainers = ({ children }) => {
  const isReviewer = useIsCurrentUserReviewer();
  const currentCustomerId = useCurrentCustomerId();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const notifications = useNotificationActions();

  return (
    <ProjectStoreContainer
      isGlobal
      currentCustomerId={currentCustomerId}
      currentWorkspaceId={currentWorkspaceId}
      notifications={notifications}
    >
      {isReviewer ? children : <DataStoresContainers>{children}</DataStoresContainers>}
    </ProjectStoreContainer>
  );
};

const WorkspaceStoreContainerWrapper = ({ children }) => {
  const currentUserId = useCurrentUserId();
  const currentCustomerId = useCurrentCustomerId();
  const isReviewer = useIsCurrentUserReviewer();
  const notifications = useNotificationActions();

  return (
    <WorkspaceStoreContainer
      isGlobal
      currentUserId={currentUserId}
      currentCustomerId={currentCustomerId}
      isReviewer={isReviewer}
      notifications={notifications}
    >
      <AccountDataStoresContainers>{children}</AccountDataStoresContainers>
    </WorkspaceStoreContainer>
  );
};

const CustomerStoreContainerWrapper = ({ children }) => {
  const currentUser = useCurrentUser();

  return (
    <CustomerStoreContainer
      isGlobal
      currentUser={currentUser}
    >
      <WorkspaceStoreContainerWrapper>{children}</WorkspaceStoreContainerWrapper>
    </CustomerStoreContainer>
  );
};

const GlobalStoreContainers = ({ children }) => {
  return (
    <NotificationStoreContainer isGlobal>
      <DebugStoreContainer isGlobal>
        <UiStoreContainer isGlobal>
          <SystemStoreContainer isGlobal>
            <UserStoreContainer isGlobal>
              <CustomerStoreContainerWrapper>{children}</CustomerStoreContainerWrapper>
            </UserStoreContainer>
          </SystemStoreContainer>
        </UiStoreContainer>
      </DebugStoreContainer>
    </NotificationStoreContainer>
  );
};

export default GlobalStoreContainers;
